/**
 * Colour L0: the ten-rung ramps every colour decision in the tree resolves to, each rung spelled ONCE.
 *
 * WHY `const/` AND NOT THE BRAND TOKEN TREE. These ramps lived as module-private consts inside
 * `brand/federa/tokens/colors.ts` -- colour's L0 sitting at L2 -- and the `utils/` floor admits only `@federa/const`,
 * `@federa/types` and `zod`, so `brand/` was structurally unreachable from it. Every utils-floor leaf that needed a
 * rung therefore HAND-MIRRORED the hex: `utils/styles/semantic-colors.ts` restated ten members off four ramps and
 * `utils/styles/interaction-tokens.ts` restated two more, each guarded by a drift pin standing in for the SSOT that
 * could not exist. `const/` is the one home BOTH layers may import, so the mirrors are deleted and the relationship is
 * an IMPORT rather than a re-typed literal. Sibling of `const/brand-palette.ts` on exactly that reasoning.
 *
 * NOT A MERGE WITH `const/brand-palette.ts`, and not a second spelling of it. That leaf holds the two BRAND IDENTITY
 * hexes -- the values "the FEDERA brand" is defined by, each with named roles across layers. This leaf holds the
 * SCALES those identities sit inside. The one value both would carry, the forest green, is BOUND here from
 * `BRAND_PALETTE.primary` rather than re-typed, so `primary[600]`, `semanticColors.success` and the focus ring stay
 * one decision. `DS-BRAND-HEX-LITERAL-SSOT-001` guards that binding.
 *
 * NOT A MEMBER of `const/design-token-scales.ts` either: that leaf's own docstring excludes brand-specific values
 * (colours, font families, gradients) because it is the brand-AGNOSTIC scale SSOT.
 *
 * L0, NOT A PUBLISHED VOCABULARY. A rung is a primitive, not a token a surface renders by name. The two-rung colour
 * model (DL-1.1, recorded in `brand/federa/tokens/colors.ts`) still holds: a rung reaches a consumer only under an L1
 * semantic ALIAS, never as `--color-<family>-<rung>` emitted wholesale. Adding a ramp here publishes nothing.
 */

import { BRAND_PALETTE } from '@federa/const/brand-palette';
import type { PaletteShades } from '@federa/types/ui-design-tokens';

/** Standard 10-shade scale factory, for type safety across all ramps. */
const createPalette = <T extends PaletteShades>(shades: T): T => shades;

export const COLOR_RAMPS = {
	/** The ONE brand ramp. The 600 rung IS the brand identity green -- bound, never re-typed. */
	primary: createPalette({
		50: '#F1F8F4',
		100: '#E8F5EE',
		200: '#D4E9E2',
		300: '#BDDCCD',
		400: '#83CFAA',
		500: '#4FA07D',
		600: BRAND_PALETTE.primary,
		700: '#0A5A30',
		800: '#083E22',
		900: '#052516',
	} as const),

	neutral: createPalette({
		50: '#F9FAFA',
		100: '#F3F4F4',
		200: '#E0E3E7',
		300: '#D4D7DB',
		400: '#9CA3AF',
		500: '#6B7280',
		600: '#4B5563',
		700: '#374151',
		800: '#1F2937',
		900: '#0F291D',
	} as const),

	/**
	 * TRUE neutral: every rung a grey with equal channels. Beside `neutral`, not instead of it, because `neutral` is
	 * not neutral -- it leans cool throughout and its 900 rung is a green-black, which is right for the expression it
	 * was drawn for and wrong for one that is black and white (Noor, 2026-09-17: "black and white, colour only for
	 * status"). An expression binds ONE of the two as its grey; nothing binds both.
	 *
	 * L0, so it lives here with the other ramps rather than inside the expression that first needed it: a rung is a
	 * primitive any expression may bind, and `DS-COLOR-RAMP-HEX-LITERAL-SSOT-001` guards every ramp in this record
	 * with no edit there. Measured the day it landed: none of the ten hexes occurs anywhere else in the tracked tree
	 * outside two suite fixtures, so it arrived with an empty queue.
	 */
	ink: createPalette({
		50: '#FAFAFA',
		100: '#F5F5F5',
		200: '#E5E5E5',
		300: '#D4D4D4',
		400: '#A3A3A3',
		500: '#737373',
		600: '#525252',
		700: '#404040',
		800: '#262626',
		900: '#171717',
	} as const),

	/** Emerald/mint, DISTINCT from `primary` on purpose -- the brand line is a desaturated forest green. */
	green: createPalette({
		50: '#ECFDF5',
		100: '#D1FAE5',
		200: '#A7F3D0',
		300: '#6EE7B7',
		400: '#34D399',
		500: '#10B981',
		600: '#059669',
		700: '#047857',
		800: '#065F46',
		900: '#064E3B',
	} as const),

	yellow: createPalette({
		50: '#FFFBEB',
		100: '#FEF3C7',
		200: '#FDE68A',
		300: '#FCD34D',
		400: '#FBBF24',
		500: '#F59E0B',
		600: '#D97706',
		700: '#B45309',
		800: '#92400E',
		900: '#78350F',
	} as const),

	red: createPalette({
		50: '#FEF2F2',
		100: '#FEE2E2',
		200: '#FECACA',
		300: '#FCA5A5',
		400: '#F87171',
		500: '#EF4444',
		600: '#DC2626',
		700: '#B91C1C',
		800: '#991B1B',
		900: '#7F1D1D',
	} as const),

	blue: createPalette({
		50: '#EFF6FF',
		100: '#DBEAFE',
		200: '#BFDBFE',
		300: '#93C5FD',
		400: '#60A5FA',
		500: '#3B82F6',
		600: '#2563EB',
		700: '#1D4ED8',
		800: '#1E40AF',
		900: '#1E3A8A',
	} as const),
} as const;
